import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  AuthService,
  ContextService,
  getRouterRequiredPermission,
  InitializingComponent,
  PortalFirmGroupOption,
  redirectToLoginWhenSignedOut,
  User
} from '@lr/core';
import { AppSnackbarComponent, HeaderComponent } from '@lr/ui';
import { FirmSelectorComponent } from './components/firm-selector/firm-selector.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FirmSelectorComponent,
    UserMenuComponent,
    AppSnackbarComponent,
    InitializingComponent
  ],
  template: `
    <div class="app-container">
      @if (showLayout()) {
        <lib-header
          [title]="'Attorney Portal'"
          [userName]="userName()"
          [userEmail]="userEmail()"
          [logoUrl]="'assets/legal-resources-logo.png'"
          [showMenuButton]="false"
          [compact]="true"
          (loginClick)="login()"
          (logoutClick)="logout()">
          <app-firm-selector
            headerCenter
            [firms]="firms()"
            [selectedFirmId]="selectedFirmId()"
            (firmChange)="onFirmChange($event)">
          </app-firm-selector>
          <app-user-menu
            headerEnd
            [userName]="userName()"
            [userEmail]="userEmail()"
            [userRoles]="userRoles()"
            (logoutClick)="logout()">
          </app-user-menu>
        </lib-header>

        <main class="main-content">
          <router-outlet></router-outlet>
        </main>
      } @else if (showBootLoading()) {
        <div class="full-screen-content">
          <lr-initializing />
        </div>
      } @else {
        <div class="full-screen-content">
          <router-outlet></router-outlet>
        </div>
      }
      <lib-app-snackbar />
    </div>
  `
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private contextService = inject(ContextService);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  userName = signal<string | null>(null);
  userEmail = signal<string | null>(null);
  userRoles = signal<string[]>([]);
  currentRoute = signal('');
  currentUser = signal<User | null | undefined>(null);
  authLoading = signal(true);
  contextReady = signal(false);
  firms = signal<PortalFirmGroupOption[]>([]);
  selectedFirmId = signal<number | null>(null);

  private publicRoutes = ['/callback', '/logout', '/access-denied', '/unauthorized', '/firm-selection', '/login'];

  showLayout = computed(() => {
    if (this.authLoading() || !this.currentUser() || !this.contextReady()) {
      return false;
    }
    return this.isAuthenticatedContentRoute(this.cleanRoute());
  });

  /** Full-screen spinner until Auth0 and portal context are ready, or login redirect starts. */
  showBootLoading = computed(() => {
    if (this.isPublicRoute(this.cleanRoute())) {
      return false;
    }
    return this.authLoading() || !this.currentUser() || !this.contextReady();
  });

  constructor() {
    this.currentRoute.set(this.router.url);

    this.authService.isLoading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.authLoading.set(loading);
    });

    this.contextService.contextReady$.pipe(takeUntil(this.destroy$)).subscribe(ready => {
      this.contextReady.set(ready);
    });

    this.authService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.currentUser.set(user);
      this.userName.set(user?.name || user?.email || null);
      this.userEmail.set(user?.email || null);
    });

    this.contextService.permissions$.pipe(
      filter(permissions => permissions !== null),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.firms.set(this.contextService.getFirmGroups());
      this.refreshUserRoles();
    });

    this.contextService.selectedFirm$.pipe(takeUntil(this.destroy$)).subscribe(firm => {
      this.selectedFirmId.set(firm?.firmId ?? null);
      this.refreshUserRoles();
    });
  }

  ngOnInit(): void {
    redirectToLoginWhenSignedOut(this.authService, this.router, this.destroy$);

    this.currentRoute.set(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(event => this.currentRoute.set(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFirmChange(firmId: number): void {
    const previousFirmId = this.contextService.getSelectedFirm()?.firmId;
    this.contextService.setSelectedFirm(firmId);
    if (this.contextService.getSelectedFirm()?.firmId !== firmId || previousFirmId === firmId) {
      return;
    }

    const codes = this.contextService.getPermissions()?.permissions ?? [];
    const cleanRoute = this.router.url.split('?')[0].split('#')[0];

    if (this.isUserFormRoute(cleanRoute)) {
      const target = codes.includes('User.View') ? '/users' : '/home';
      void this.router.navigate([target], { replaceUrl: true });
      return;
    }

    const required = getRouterRequiredPermission(this.router);
    if (required && !codes.includes(required)) {
      void this.router.navigate(['/access-denied'], { replaceUrl: true });
      return;
    }

    if (this.isAuthenticatedContentRoute(cleanRoute)) {
      void this.router.navigateByUrl(this.router.url, {
        replaceUrl: true,
        onSameUrlNavigation: 'reload'
      });
    }
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    // Same as client portal: leave the current page before Auth0 clears the session.
    void this.router.navigate(['/logout']);
  }

  private refreshUserRoles(): void {
    const portalRoles = this.contextService.getRoles();
    const firmRoles = this.contextService.getSelectedFirm()?.roles ?? [];
    const roles = [...portalRoles];
    for (const role of firmRoles) {
      if (!roles.some(current => current.toLowerCase() === role.toLowerCase())) {
        roles.push(role);
      }
    }
    this.userRoles.set(roles);
  }

  private cleanRoute(): string {
    return this.currentRoute().split('?')[0].split('#')[0];
  }

  private isPublicRoute(cleanRoute: string): boolean {
    return this.publicRoutes.some(
      route => cleanRoute === route || cleanRoute.startsWith(`${route}/`)
    );
  }

  private isAuthenticatedContentRoute(cleanRoute: string): boolean {
    return !!cleanRoute && cleanRoute !== '/' && !this.isPublicRoute(cleanRoute);
  }

  private isUserFormRoute(cleanRoute: string): boolean {
    return cleanRoute === '/users/new' || /^\/users\/[^/]+$/.test(cleanRoute);
  }
}
